import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Waves, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { seedData, settingsAPI } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [settings, setSettings] = useState(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect');

  useEffect(() => {
    // Load settings for logo
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      // Settings might not exist yet
    }
  };

  const getLogoUrl = () => {
    if (!settings?.logo_url) return null;
    if (settings.logo_url.startsWith('http')) return settings.logo_url;
    return `${BACKEND_URL}${settings.logo_url}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    // Store redirect URL before login (in case URL changes during process)
    const targetUrl = redirectUrl || '/dashboard';
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      // Navigate to stored redirect URL
      console.log('[Login] Navigating to:', targetUrl);
      // Use window.location for a hard redirect to prevent React Router race conditions
      window.location.href = targetUrl;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await seedData();
      toast.success('Sample data created! Use admin@eswatiniseals.com / admin123 to login');
      setEmail('admin@eswatiniseals.com');
      setPassword('admin123');
    } catch (error) {
      if (error.response?.data?.message?.includes('already seeded')) {
        toast.info('Database already has data. Try logging in.');
        setEmail('admin@eswatiniseals.com');
        setPassword('admin123');
      } else {
        toast.error('Failed to seed data');
      }
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1730244548329-4ae2f4fcaa7c?w=1920')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary-900/95" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-4 mb-8 cursor-pointer group" data-testid="login-logo-clickable">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform">
              {getLogoUrl() ? (
                <img src={getLogoUrl()} alt="Logo" className="w-full h-full object-contain p-2" />
              ) : (
                <Waves className="w-8 h-8" />
              )}
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">{settings?.academy_name || 'Eswatini Seals'}</h1>
              <p className="text-white/80">Swim Academy</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-bold mb-4 leading-tight">
            Developing Champions<br />One Stroke at a Time
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            Professional swim training and coaching for all ages and skill levels. Join our community of confident swimmers.
          </p>
          
          {/* Decorative waves */}
          <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20">
            <svg viewBox="0 0 1440 320" className="w-full h-full">
              <path fill="currentColor" d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,213.3C1248,235,1344,213,1392,202.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8" data-testid="mobile-logo">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary-900 flex items-center justify-center text-white overflow-hidden">
              {getLogoUrl() ? (
                <img src={getLogoUrl()} alt="Logo" className="w-full h-full object-contain p-1" />
              ) : (
                <Waves className="w-6 h-6" />
              )}
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900">{settings?.academy_name || 'Eswatini Seals'}</h1>
              <p className="text-sm text-slate-500">Swim Academy</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
              <CardDescription>Sign in to your account to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    data-testid="login-email"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      data-testid="login-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary-600 rounded-full font-medium"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-sm text-slate-500 text-center mb-3">
                  Don&apos;t have an account?
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                >
                  Register as Parent/Student
                </Button>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500 text-center mb-3">
                  First time? Initialize the database with sample data
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-500"
                  onClick={handleSeedData}
                  disabled={seeding}
                  data-testid="seed-data-btn"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating sample data...
                    </>
                  ) : (
                    'Load Sample Data'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-slate-500">
            Professional swim academy management system
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
